<!-- Full-page error display with status code, message, refresh action, and debug stack traces. -->

<script lang="ts" setup>
import { RpcError } from '@nzyme/rpc/types/RpcError.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { RefreshCw } from 'lucide-vue-next';
import { computed } from 'vue';
import type { VNodeChild } from 'vue';

import { PRODUCTION } from '@sigma/env/environments.js';

import Button from './Button.vue';
import * as l from './ErrorPage.loc.js';
import StackTrace from './StackTrace.vue';

/**
 * Shape of the error object passed to ErrorPage.
 * Compatible with NuxtError and similar error types.
 */
export interface ErrorPageError {
  statusCode?: number;
  statusMessage?: string;
  message?: string;
  stack?: string;
  cause?: unknown;
}

interface Props {
  /**
   * The error object to display.
   * When provided, statusCode, title, description, debugError, clientStackTrace,
   * and serverStackTrace are derived automatically.
   */
  error?: ErrorPageError | null;
  statusCode?: number;
  title?: string;
  description?: string;
  showRefresh?: boolean;
  debugError?: {
    error: string;
    message: string;
  } | null;
  clientStackTrace?: string | null;
  serverStackTrace?: string | null;
}

interface Slots {
  actions: () => VNodeChild;
}

const props = withDefaults(defineProps<Props>(), {
  statusCode: 500,
  showRefresh: true,
});

defineSlots<Slots>();

const t = useTranslate();

const debug = !PRODUCTION;

const resolvedStatusCode = computed(() => {
  return props.error?.statusCode ?? props.statusCode;
});

const resolvedTitle = computed(() => {
  return props.title ?? props.error?.statusMessage;
});

const resolvedDescription = computed(() => {
  return props.description ?? props.error?.message;
});

const resolvedDebugError = computed(() => {
  if (props.debugError !== undefined) {
    return props.debugError;
  }

  const cause = props.error?.cause;
  if (!cause || !debug) {
    return null;
  }

  if (cause instanceof RpcError) {
    return {
      error: `RpcError: ${cause.data.error}`,
      message: cause.data.message,
    };
  }

  if (cause instanceof Error) {
    return {
      error: cause.name,
      message: cause.message,
    };
  }

  return null;
});

const resolvedClientStackTrace = computed(() => {
  if (props.clientStackTrace !== undefined) {
    return props.clientStackTrace;
  }

  if (!debug || !props.error?.stack) {
    return null;
  }

  return props.error.stack;
});

const resolvedServerStackTrace = computed(() => {
  if (props.serverStackTrace !== undefined) {
    return props.serverStackTrace;
  }

  const cause = props.error?.cause;
  if (debug && cause instanceof RpcError && cause.data.stack) {
    return cause.data.stack;
  }

  return null;
});

const errorInfo = computed(() => {
  const title = resolvedTitle.value;
  const description = resolvedDescription.value;

  if (title && description) {
    return { title, description };
  }

  switch (resolvedStatusCode.value) {
    case 404:
      return {
        title: title ?? t(l.pageNotFoundTitle),
        description: description ?? t(l.pageNotFoundDescription),
      };
    case 500:
      return {
        title: title ?? t(l.internalServerErrorTitle),
        description: description ?? t(l.internalServerErrorDescription),
      };
    case 503:
      return {
        title: title ?? t(l.serviceUnavailableTitle),
        description: description ?? t(l.serviceUnavailableDescription),
      };
    default:
      return {
        title: title ?? t(l.unexpectedErrorTitle),
        description: description ?? t(l.unexpectedErrorDescription),
      };
  }
});

const showRefreshButton = computed(() => {
  return props.showRefresh && resolvedStatusCode.value !== 404;
});

function refresh() {
  window.location.reload();
}
</script>

<template>
  <div :class="css.content">
    <div
      v-if="resolvedStatusCode"
      :class="css.errorCode"
    >
      {{ resolvedStatusCode }}
    </div>

    <div :class="css.info">
      <h1 :class="css.title">
        {{ errorInfo.title }}
      </h1>

      <p :class="css.description">
        {{ errorInfo.description }}
      </p>
    </div>

    <div :class="css.actions">
      <Button
        v-if="showRefreshButton"
        variant="outline"
        color="black"
        size="xlarge"
        rounded
        :icon="RefreshCw"
        @click="refresh()"
      >
        {{ t(l.refresh) }}
      </Button>

      <slot name="actions" />
    </div>

    <div
      v-if="resolvedDebugError"
      :class="css.debugInfo"
    >
      <div :class="css.debugInfoField">
        <span :class="css.debugInfoLabel">Error:</span>
        <span :class="css.debugInfoMessage">{{ resolvedDebugError.error }}</span>
      </div>
      <div :class="css.debugInfoField">
        <span :class="css.debugInfoLabel">Message:</span>
        <span :class="css.debugInfoMessage">{{ resolvedDebugError.message }}</span>
      </div>
    </div>

    <StackTrace
      v-if="resolvedServerStackTrace"
      :stack="resolvedServerStackTrace"
      title="Server stack trace"
    />

    <StackTrace
      v-if="resolvedClientStackTrace"
      :stack="resolvedClientStackTrace"
      title="Client stack trace"
    />
  </div>
</template>

<style lang="scss" module="css">
.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  width: 100%;
  gap: $spacing-24;
}

.errorCode {
  font-size: 120px;
  font-weight: $font-weight-700;
  line-height: 1;
  margin: 0;

  @include media-max-sm {
    font-size: 80px;
  }
}

.info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing-12;
  text-align: center;
}

.title {
  font-size: $font-size-32;
  font-weight: $font-weight-700;
  margin: 0;

  @include media-max-sm {
    font-size: $font-size-24;
  }
}

.description {
  font-size: $font-size-16;
  color: $color-gray-600;
  margin: 0;
  line-height: 1.5;

  @include media-max-sm {
    font-size: $font-size-14;
  }
}

.actions {
  display: flex;
  flex-direction: column;
  gap: $spacing-12;

  @include media-min-sm {
    flex-direction: row;
  }

  &:not(:last-child) {
    margin-bottom: $spacing-30;
  }
}

.debugInfo {
  width: 100%;
  background: $color-white;
  border: 1px solid $color-red-200;
  border-radius: $border-radius-08;
  padding: $spacing-16;
  display: flex;
  flex-direction: column;
  gap: $spacing-12;
}

.debugInfoField {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
}

.debugInfoLabel {
  font-size: $font-size-12;
  font-weight: $font-weight-600;
  color: $color-gray-600;
  text-transform: uppercase;
  letter-spacing: $letter-spacing-05;
}

.debugInfoMessage {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: $font-size-14;
  color: $color-red-600;
  word-break: break-word;
}
</style>
